import { defineStore } from 'pinia'
import { Code, Dictionary as SystemDictionary, DictionaryCodeTypeEnum } from '@/api/system/dictionary'
import { getDayjs, isBoolean, isNumber } from '@/common/utils/common'

export type Dictionary = {
    label: string
    key: any
    value: any
    sort: number
    isEnable: number
    categoryCode: string
}

const getExpiresIn = () => {
    return getDayjs().add(1, 'hour').valueOf()
}

const groupBy = (dictionaries: Dictionary[]): {} => {
    const group = {}
    for (const item of dictionaries) {
        if (group[item.categoryCode]) {
            group[item.categoryCode].push(item)
        } else {
            group[item.categoryCode] = [item]
        }
    }
    return group
}

const getDictionaries = async (noCacheCategoryCodeList: string[], noCacheKeyList?: string[]): Promise<Dictionary[]> => {
    const dictionaries: Dictionary[] = []

    const codeList: Code[] = []
    if (noCacheCategoryCodeList) {
        for (const key of noCacheCategoryCodeList) {
            codeList.push({
                type: DictionaryCodeTypeEnum.CATEGORY_CODE,
                code: key,
            })
        }
    }
    if (noCacheKeyList) {
        for (const key of noCacheKeyList) {
            codeList.push({
                type: DictionaryCodeTypeEnum.KEY,
                code: key,
            })
        }
    }

    const result = await SystemDictionary.list({
        codeList: codeList,
    })
    const data = result.data
    data
        ?.filter(item => noCacheCategoryCodeList.includes(item.categoryCode))
        .forEach(item => {
            const value = item.value
            const value2 = isBoolean(value) ? Boolean(value) : isNumber(value) ? Number.parseFloat(value) : value
            const dic: Dictionary = {
                label: item.name,
                key: item.key,
                value: value2,
                sort: item.sort,
                isEnable: item.isEnable,
                categoryCode: item.categoryCode,
            }
            dictionaries.push(dic)
        })

    const alreadyCategoryCodeList = dictionaries.map(item => item.categoryCode)
    if (noCacheKeyList && noCacheKeyList.length > 0) {
        data
            ?.filter(item => !alreadyCategoryCodeList.includes(item.categoryCode) && noCacheKeyList.includes(item.key))
            .forEach(item => {
                const value = item.value
                const value2 = isBoolean(value) ? Boolean(value) : isNumber(value) ? Number.parseFloat(value) : value
                const dic: Dictionary = {
                    label: item.name,
                    key: item.key,
                    value: value2,
                    sort: item.sort,
                    isEnable: item.isEnable,
                    categoryCode: item.categoryCode,
                }
                dictionaries.push(dic)
            })
    }
    return dictionaries
}

export type DictionaryState = {
    map: Record<
        string,
        {
            expiresIn: number
            dictionaries: Dictionary[]
        }
    >
    keyMap: Record<string, Dictionary>
}

export const useDictionaryStore = defineStore('dictionary', {
    state: (): DictionaryState => {
        return {
            map: {},
            keyMap: {},
        }
    },
    actions: {
        async push(dictionaries: Dictionary[]) {
            await this.expires()
            const group = groupBy(dictionaries)
            for (const categoryCode in group) {
                if (categoryCode && categoryCode !== '') {
                    const dictionaryMap = this.map[categoryCode]
                    if (!dictionaryMap) {
                        this.map[categoryCode] = {
                            expiresIn: getExpiresIn(),
                            dictionaries: group[categoryCode],
                        }
                    }
                }
            }

            // 缓存 key=dict
            if (this.map) {
                for (const categoryCode in this.map) {
                    if (categoryCode && categoryCode !== '') {
                        for (const index in this.map[categoryCode]?.dictionaries) {
                            const dict = this.map[categoryCode]?.dictionaries[index]
                            if (dict.key && dict.key !== '') {
                                this.keyMap[dict.key] = dict
                            }
                        }
                    }
                }
            }
        },
        async pop(categoryCode: string) {
            const dicts = this.map[categoryCode]?.dictionaries
            if (dicts) {
                for (const dictKey in dicts) {
                    delete this.keyMap[dictKey]
                }
            }
            delete this.map[categoryCode]
        },
        async pull(categoryCodeList: string[], orKeyList?: string[]): Promise<Record<string, Dictionary[]>> {
            await this.expires()
            const finalDicts: Record<string, Dictionary[]> = {}
            const noCacheCategoryCodes: string[] = []

            // 用 dict code 取缓存的多条 dict
            categoryCodeList.forEach(categoryCode => {
                const dictionaryMap = this.map[categoryCode]
                if (dictionaryMap) {
                    finalDicts[categoryCode] = dictionaryMap.dictionaries || []
                } else {
                    noCacheCategoryCodes.push(categoryCode)
                }
            })

            // 缓存 key=dict
            if (this.map) {
                for (const key in this.map) {
                    for (const index in this.map[key]?.dictionaries) {
                        const dict = this.map[key]?.dictionaries[index]
                        this.keyMap[dict.key] = dict
                    }
                }
            }

            // 用 dict key 取单条 dict
            const noCacheKeys: string[] = []
            const finalKeyDicts = {}
            if (orKeyList) {
                orKeyList.forEach(key => {
                    const dict = this.keyMap[key]
                    if (dict) {
                        finalKeyDicts[key] = dict
                    } else {
                        noCacheKeys.push(key)
                    }
                })
            }

            if (noCacheCategoryCodes.length === 0 && noCacheKeys.length === 0) {
                return finalDicts
            }

            console.log(noCacheCategoryCodes, noCacheKeys)
            const remoteDictionaries = await getDictionaries(noCacheCategoryCodes, noCacheKeys)

            noCacheCategoryCodes.forEach(noCategoryCode => {
                const any = remoteDictionaries.filter(item => item.categoryCode === noCategoryCode)
                if (!any || any.length <= 0) {
                    remoteDictionaries.push({
                        categoryCode: noCategoryCode,
                        label: '',
                        key: '',
                        value: '',
                        sort: 0,
                        isEnable: 1,
                    })
                }
            })

            noCacheKeys.forEach(noKey => {
                const any = remoteDictionaries.filter(item => item.key === noKey)
                if (!any || any.length <= 0) {
                    remoteDictionaries.push({
                        categoryCode: '',
                        label: '',
                        key: noKey,
                        value: '',
                        sort: 0,
                        isEnable: 1,
                    })
                }
            })

            // console.log(remoteDictionaries)
            await this.push(remoteDictionaries)
            noCacheCategoryCodes.forEach(noCategoryCode => {
                const dictionaryMap = this.map[noCategoryCode]
                if (dictionaryMap) {
                    finalDicts[noCategoryCode] = dictionaryMap.dictionaries || []
                } else {
                    finalDicts[noCategoryCode] = []
                }
            })
            return finalDicts
        },
        async expires() {
            if (this.map) {
                for (const dictionaryCategoryCode in this.map) {
                    const isExpires = getDayjs().isAfter(this.map[dictionaryCategoryCode].expiresIn)
                    if (isExpires) {
                        await this.pop(dictionaryCategoryCode)
                    }
                }
            }
        },
        reset() {
            this.$reset()
        },
        clear() {
            sessionStorage.removeItem('dictionary')
        }
    },
    persist: {
        storage: sessionStorage,
    },
})
